import type { ReactNode } from 'react'

/**
 * 工具栏图标：统一 24x24 视窗、currentColor 填充，风格对齐 Google Docs 用的
 * Material 系图标（这些是通用几何图形，不引第三方图标库）。
 */

function Icon({ children, size = 20 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  )
}

export function CaretDownIcon() {
  return (
    <Icon size={14}>
      <path d="M7 9.5 12 15l5-5.5z" />
    </Icon>
  )
}

export function UndoIcon() {
  return (
    <Icon>
      <path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z" />
    </Icon>
  )
}

export function RedoIcon() {
  return (
    <Icon>
      <path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.05-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z" />
    </Icon>
  )
}

export function BoldIcon() {
  return (
    <Icon>
      <path d="M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z" />
    </Icon>
  )
}

export function ItalicIcon() {
  return (
    <Icon>
      <path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z" />
    </Icon>
  )
}

export function UnderlineIcon() {
  return (
    <Icon>
      <path d="M12 17c3.31 0 6-2.69 6-6V3h-2.5v8c0 1.93-1.57 3.5-3.5 3.5S8.5 12.93 8.5 11V3H6v8c0 3.31 2.69 6 6 6zm-7 2v2h14v-2H5z" />
    </Icon>
  )
}

/**
 * 文字颜色 / 突出显示：图标本体只画字形，下方那条会随颜色变化的小横条由
 * Toolbar 里的 .tb-color-bar 负责（对齐 Google Docs 的观感）。
 * 早先图标里自带一条下划线，和动态横条叠成了两条，已去掉。
 */
export function TextColorIcon() {
  return (
    <Icon>
      <g transform="translate(0 2)">
        <path d="M11 3 5.5 17h2.25l1.12-3h6.25l1.12 3h2.25L13 3h-2zm-1.38 9L12 5.67 14.38 12H9.62z" />
      </g>
    </Icon>
  )
}

export function HighlightIcon() {
  return (
    <Icon>
      <g transform="translate(0 1.5)">
        <path d="M6 14.5 14.5 6l3.5 3.5L9.5 18H6v-3.5z" />
      </g>
    </Icon>
  )
}

export function LinkIcon() {
  return (
    <Icon>
      <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z" />
    </Icon>
  )
}

export function CommentIcon() {
  return (
    <Icon>
      <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
    </Icon>
  )
}

export function ImageIcon() {
  return (
    <Icon>
      <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
    </Icon>
  )
}

export function AlignLeftIcon() {
  return (
    <Icon>
      <path d="M15 15H3v2h12v-2zm0-8H3v2h12V7zM3 13h18v-2H3v2zm0 8h18v-2H3v2zM3 3v2h18V3H3z" />
    </Icon>
  )
}

export function AlignCenterIcon() {
  return (
    <Icon>
      <path d="M7 15v2h10v-2H7zm-4 6h18v-2H3v2zm0-8h18v-2H3v2zm4-6v2h10V7H7zM3 3v2h18V3H3z" />
    </Icon>
  )
}

export function AlignRightIcon() {
  return (
    <Icon>
      <path d="M3 21h18v-2H3v2zm6-4h12v-2H9v2zm-6-4h18v-2H3v2zm6-4h12V7H9v2zM3 3v2h18V3H3z" />
    </Icon>
  )
}

export function AlignJustifyIcon() {
  return (
    <Icon>
      <path d="M3 21h18v-2H3v2zm0-4h18v-2H3v2zm0-4h18v-2H3v2zm0-4h18V7H3v2zm0-6v2h18V3H3z" />
    </Icon>
  )
}

export function LineSpacingIcon() {
  return (
    <Icon>
      <path d="M6 7h2.5L5 3.5 1.5 7H4v10H1.5L5 20.5 8.5 17H6V7zm4-2v2h12V5H10zm0 14h12v-2H10v2zm0-6h12v-2H10v2z" />
    </Icon>
  )
}

export function ChecklistIcon() {
  return (
    <Icon>
      <path d="M22 7h-9v2h9V7zm0 8h-9v2h9v-2zM5.54 11 2 7.46l1.41-1.41 2.12 2.12 4.24-4.24 1.41 1.41L5.54 11zm0 8L2 15.46l1.41-1.41 2.12 2.12 4.24-4.24 1.41 1.41L5.54 19z" />
    </Icon>
  )
}

export function BulletListIcon() {
  return (
    <Icon>
      <path d="M4 10.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm0-6a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm0 12a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z" />
    </Icon>
  )
}

export function NumberedListIcon() {
  return (
    <Icon>
      <path d="M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1zm5-6v2h14V5H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z" />
    </Icon>
  )
}

export function ClearFormatIcon() {
  return (
    <Icon>
      <path d="M3.27 5 2 6.27l6.97 6.97L6.5 19h3l1.57-3.66L16.73 21 18 19.73 3.55 5.27 3.27 5zM6 5v.18L8.82 8h2.4l-.72 1.68 2.1 2.1L14.21 8H20V5H6z" />
    </Icon>
  )
}
